/**
 * 统一事件总线
 * 替代分散的 emitter 模式，提供类型化的事件管理
 */

// 事件类型常量
export const SDK_EVENTS = {
  // TIM 事件
  SDK_READY: 'SDK_READY',
  SDK_NOT_READY: 'SDK_NOT_READY',
  KICKED_OUT: 'KICKED_OUT',
  TIM_ERROR: 'TIM_ERROR',
  MESSAGE_RECEIVED: 'MESSAGE_RECEIVED',
  CONVERSATION_LIST_UPDATED: 'CONVERSATION_LIST_UPDATED',
  MESSAGE_READ_BY_PEER: 'MESSAGE_READ_BY_PEER',
  MESSAGE_REVOKED: 'MESSAGE_REVOKED',

  // TRTC 事件
  REMOTE_USER_ENTER: 'REMOTE_USER_ENTER',
  REMOTE_USER_LEAVE: 'REMOTE_USER_LEAVE',
  REMOTE_VIDEO_AVAILABLE: 'REMOTE_VIDEO_AVAILABLE',
  REMOTE_AUDIO_AVAILABLE: 'REMOTE_AUDIO_AVAILABLE',
  NETWORK_QUALITY: 'NETWORK_QUALITY',
  TRTC_ERROR: 'TRTC_ERROR',

  // 通话信令事件
  CALL_SIGNAL: 'CALL_SIGNAL',

  // 业务事件
  TOGGLE_SPEAKER: 'TOGGLE_SPEAKER'
}

class EventBus {
  constructor() {
    this._listeners = new Map()
  }

  /**
   * 订阅事件
   * @param {string} event - 事件名
   * @param {Function} callback - 回调函数
   * @returns {Function} 取消订阅函数
   */
  on(event, callback) {
    if (!this._listeners.has(event)) {
      this._listeners.set(event, new Set())
    }
    this._listeners.get(event).add(callback)

    // 返回取消订阅函数
    return () => this.off(event, callback)
  }

  /**
   * 订阅一次性事件
   * @param {string} event - 事件名
   * @param {Function} callback - 回调函数
   */
  once(event, callback) {
    const wrapper = (data) => {
      this.off(event, wrapper)
      callback(data)
    }
    this.on(event, wrapper)
  }

  /**
   * 取消订阅
   * @param {string} event - 事件名
   * @param {Function} callback - 回调函数
   */
  off(event, callback) {
    if (!this._listeners.has(event)) return
    this._listeners.get(event).delete(callback)
  }

  /**
   * 触发事件
   * @param {string} event - 事件名
   * @param {*} data - 事件数据
   */
  emit(event, data) {
    if (!this._listeners.has(event)) return
    this._listeners.get(event).forEach(callback => {
      try {
        callback(data)
      } catch (error) {
        console.error(`[EventBus] Error in event handler for ${event}:`, error)
      }
    })
  }

  /**
   * 移除事件的所有监听器
   * @param {string} event - 事件名（不传则移除所有）
   */
  removeAllListeners(event) {
    if (event) {
      this._listeners.delete(event)
    } else {
      this._listeners.clear()
    }
  }

  /**
   * 获取事件监听器数量
   * @param {string} event - 事件名
   */
  listenerCount(event) {
    return this._listeners.get(event)?.size || 0
  }
}

// 单例导出
export const eventBus = new EventBus()

// 便捷方法：等待特定事件
export function waitForEvent(event, timeout = 10000) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      eventBus.off(event, handler)
      reject(new Error(`Timeout waiting for event: ${event}`))
    }, timeout)

    const handler = (data) => {
      clearTimeout(timer)
      resolve(data)
    }

    eventBus.on(event, handler)
  })
}

export default eventBus
